import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Phone, MessageSquare, Inbox, Package, MapPin, User, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import CockpitLayout from "@/components/admin/CockpitLayout";
import LoadMore from "@/components/admin/LoadMore";
import { GoldAction, GoldLoading, GoldError } from "@/components/admin/GoldUi";
import { api } from "@/lib/api";
import { useCursorList } from "@/lib/useCursorList";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/perms";

const STATUS = { new: "Neu", contacted: "Kontaktiert", closed: "Erledigt" };
const ACTION = { new: "Als neu markieren", contacted: "Als kontaktiert markieren", closed: "Als erledigt markieren" };
const SOURCE = { form: "Formular", whatsapp: "WhatsApp", service: "Dienstleistung", contact: "Kontakt", "ai-chat": "KI-Chat" };
const DOT = { new: "#D89A00", contacted: "#4A90B8", closed: "#7B8C73" };

export default function Inquiries() {
  const { user } = useAuth();
  const canManage = can(user, "inquiry:manage");
  const [filter, setFilter] = useState("");
  const [inquiriesNew, setInquiriesNew] = useState(0);
  const [pending, setPending] = useState(new Set());
  const pendingRef = useRef(new Set());
  const loadBadge = useCallback(() => {
    api.get("/items/stats").then(({ data }) => setInquiriesNew(data.inquiries_new || 0)).catch(() => {});
  }, []);
  const params = useMemo(() => filter ? { status: filter } : {}, [filter]);
  const { items, loading, loadingMore, error, hasMore, loadMore, reload } = useCursorList("/inquiries", "inquiries", params);
  useEffect(() => { loadBadge(); }, [loadBadge]);
  const setStatus = async (inq, status) => {
    if (!canManage || pendingRef.current.has(inq.id)) return;
    pendingRef.current.add(inq.id); setPending(new Set(pendingRef.current));
    try {
      await api.put("/inquiries/" + inq.id, { status });
      toast.success("Status: " + STATUS[status]);
      await reload(); loadBadge();
    } catch { toast.error("Fehler beim Aktualisieren."); }
    finally { pendingRef.current.delete(inq.id); setPending(new Set(pendingRef.current)); }
  };
  return <CockpitLayout active="anfragen" title="Anfragen" inquiriesNew={inquiriesNew}>
    <div className="fundio-page-stack">
      <div className="fundio-section-head">
        <div><h1 className="fundio-page-title" data-testid="anfragen-title">Anfragen</h1><p className="mt-2 fundio-muted">Kontakt aufnehmen und den Stand klar festhalten.</p></div>
        <p className="fundio-note flex items-center gap-2"><Phone size={20} aria-hidden="true" />Antworten erfolgen per Telefon oder WhatsApp.</p>
      </div>
      <div className="fundio-inquiry-filters" role="group" aria-label="Anfragen nach Status filtern">
        {["", "new", "contacted", "closed"].map((f) => <button type="button" key={f || "all"} aria-pressed={filter === f} onClick={() => setFilter(f)} data-testid={"inq-filter-" + (f || "all")}>{f ? STATUS[f] : "Alle"}</button>)}
      </div>
      {loading ? <GoldLoading /> : error && items.length === 0 ? <GoldError testid="inquiries-error" message="Anfragen konnten nicht geladen werden." onRetry={reload} /> : items.length === 0 ? <div className="fundio-panel fundio-state" data-testid="inquiries-empty"><Inbox aria-hidden="true" /><h2 className="fundio-section-title">Keine Anfragen</h2><p>{filter ? "Für diesen Filter gibt es keine Anfragen." : "Anfragen von der Website erscheinen hier."}</p></div> : <div className="space-y-4" data-testid="inquiry-list">
        {items.map((inq) => {
          const phone = String(inq.phone || "").trim();
          const digits = phone.replace(/[^0-9]/g, "");
          const busy = pending.has(inq.id);
          return <article key={inq.id} className="fundio-panel" data-testid={"inquiry-card-" + inq.id} aria-busy={busy}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="fundio-status"><span className="fundio-status-dot" aria-hidden="true" style={{ background: DOT[inq.status] || "#777F88" }} />{STATUS[inq.status] || inq.status || "—"}</span>
              <span className="fundio-status">{SOURCE[inq.source] || inq.source || "—"}</span>
              {inq.location_name && <span className="fundio-status" data-testid={"inquiry-location-" + inq.id}><MapPin size={16} aria-hidden="true" />{inq.location_name}</span>}
            </div>
            <div className="fundio-inquiry-body">
              <div className="fundio-inquiry-meta">
                <h2 className="fundio-section-title"><User size={20} aria-hidden="true" />{inq.name || "Name nicht angegeben"}</h2>
                {inq.item_title && <p><Package size={20} aria-hidden="true" />{inq.item_title}</p>}
                <p><Phone size={20} aria-hidden="true" />{phone || "Keine Telefonnummer angegeben"}</p>
              </div>
              <div className="fundio-message"><MessageSquare className="mb-2" size={20} aria-hidden="true" />{inq.message || "Keine Nachricht angegeben."}</div>
            </div>
            <div className="fundio-inquiry-actions">
              <div className="fundio-contact-actions" role="group" aria-label="Kontakt aufnehmen">
                {digits ? <><GoldAction href={"tel:" + phone}><Phone size={18} aria-hidden="true" />Anrufen</GoldAction><GoldAction href={"https://wa.me/" + digits} target="_blank" rel="noreferrer"><MessageSquare size={18} aria-hidden="true" />WhatsApp</GoldAction></> : <><GoldAction disabled><Phone size={18} aria-hidden="true" />Anrufen</GoldAction><GoldAction disabled><MessageSquare size={18} aria-hidden="true" />WhatsApp</GoldAction></>}
              </div>
              <div className="fundio-status-actions" role="group" aria-label="Status festhalten">
                {canManage ? Object.keys(STATUS).filter((s) => s !== inq.status).map((s) => <GoldAction key={s} disabled={busy} data-testid={"inq-set-" + s + "-" + inq.id} onClick={() => setStatus(inq, s)}>{busy ? <Loader2 size={18} className="animate-spin" aria-hidden="true" /> : <Check size={18} aria-hidden="true" />}{ACTION[s]}</GoldAction>) : <p className="fundio-muted text-sm">Deine Rolle kann Anfragen lesen. Statusänderungen übernimmt ein Mitarbeiter.</p>}
              </div>
            </div>
          </article>;
        })}
      </div>}
      <LoadMore hasMore={hasMore} loading={loadingMore} error={error} onLoad={loadMore} testid="inquiries-load-more" />
    </div>
  </CockpitLayout>;
}
